import React from "react";
import styles from "./style.scss";
import { Streamdown } from "streamdown";
import { ReferenceTooltip } from "./ReferenceToolTip";
import { Reference as ReferenceType } from "models/AiResearch";
import { formatDate } from "utils/helper/day";

interface Props {
  text: string;
  onClickReference?: (id: number) => void;
  selectedReference?: ReferenceType | null;
  references?: ReferenceType[];
}

const skipElementTypes = new Set(["code", "pre", "a"]);

const buildReferenceTooltipContent = (title: string, meta?: string) => (
  <span className={styles.content}>
    {meta ? <span className={styles.meta}>{meta}</span> : null}
    <span className={styles.title}>{title}</span>
  </span>
);

const getReferenceTooltipContent = (
  referenceId: number,
  selectedReference?: ReferenceType | null,
  references?: ReferenceType[]
): React.ReactNode => {
  const reference =
    selectedReference?.citationNumber === referenceId
      ? selectedReference
      : references?.find(item => item.citationNumber === referenceId);

  if (!reference) return null;

  switch (reference.dataType) {
    case "article_chunk": {
      const date = reference.data.publishDate
        ? formatDate(String(reference.data.publishDate), "YYYY年MM月DD日")
        : "";
      const meta = [date, reference.data.mediaName].filter(Boolean).join("　");
      return buildReferenceTooltipContent(reference.data.title, meta);
    }
    case "financial_chart":
    case "macro_chart":
    case "macro_ir_chart": {
      const meta = [
        reference.data.meta.companyName,
        reference.data.meta.industryName
      ]
        .filter(Boolean)
        .join(" / ");
      return buildReferenceTooltipContent(reference.data.meta.name, meta);
    }
    case "financial_table": {
      const title = reference.data.content.name || "";
      return buildReferenceTooltipContent(title);
    }
    case "company_list_table": {
      const title = reference.data.content.name || "";
      return buildReferenceTooltipContent(title);
    }
    default:
      return null;
  }
};

type RendererProps = {
  children?: React.ReactNode;
  node?: unknown;
  [key: string]: unknown;
};

const renderReferenceButtonsInString = (
  value: string,
  onClickReference?: (id: number) => void,
  selectedReference?: ReferenceType | null,
  references?: ReferenceType[]
): React.ReactNode => {
  return value.split(/(\[\d+\])/g).map((part, index) => {
    const match = part.match(/^\[(\d+)\]$/);
    if (!match) return part;

    const referenceId = Number(match[1]);
    const tooltipContent = getReferenceTooltipContent(
      referenceId,
      selectedReference,
      references
    );
    return (
      <ReferenceTooltip
        key={`ref-${index}-${referenceId}`}
        content={tooltipContent}
      >
        <button
          className={styles.referenceButton}
          data-reference-button="true"
          aria-label={`Reference ${referenceId}`}
          type="button"
          onClick={() => onClickReference?.(referenceId)}
        >
          {referenceId}
        </button>
      </ReferenceTooltip>
    );
  });
};

const replaceReferences = (
  node: React.ReactNode,
  onClickReference?: (id: number) => void,
  selectedReference?: ReferenceType | null,
  references?: ReferenceType[]
): React.ReactNode => {
  if (typeof node === "string") {
    return renderReferenceButtonsInString(
      node,
      onClickReference,
      selectedReference,
      references
    );
  }

  if (Array.isArray(node)) {
    return node.map(item =>
      replaceReferences(item, onClickReference, selectedReference, references)
    );
  }

  if (React.isValidElement(node)) {
    if (typeof node.type === "string" && skipElementTypes.has(node.type)) {
      return node;
    }

    const currentChildren = (node.props as { children?: React.ReactNode })
      .children;
    const nextChildren = replaceReferences(
      currentChildren,
      onClickReference,
      selectedReference,
      references
    );
    return React.cloneElement(node, undefined, nextChildren);
  }

  return node;
};

const createReferenceRenderer = (
  tag: string,
  onClickReference?: (id: number) => void,
  selectedReference?: ReferenceType | null,
  references?: ReferenceType[]
) => {
  return ({ children, node: _, ...props }: RendererProps) =>
    React.createElement(
      tag,
      props,
      replaceReferences(
        children,
        onClickReference,
        selectedReference,
        references
      )
    );
};

export const AiResearchText: React.FC<Props> = ({
  text,
  onClickReference,
  selectedReference,
  references
}) => {
  const renderTable = ({
    children,
    node: _,
    className,
    ...props
  }: RendererProps & { className?: string }) => (
    <div className={styles.tableWrapper}>
      <table
        {...props}
        className={
          className ? `${className} ${styles.vsTable}` : styles.vsTable
        }
      >
        {children}
      </table>
    </div>
  );

  const referenceRenderer = (tag: string) =>
    createReferenceRenderer(
      tag,
      onClickReference,
      selectedReference,
      references
    );

  const referenceTags = [
    "p",
    "li",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "strong",
    "em",
    "del",
    "blockquote",
    "ul",
    "ol",
    "thead",
    "tbody",
    "tfoot",
    "tr",
    "th",
    "td",
    "span",
    "div"
  ] as const;

  const components = referenceTags.reduce((acc, tag) => {
    acc[tag] = referenceRenderer(tag);
    return acc;
  }, {} as Record<string, React.ComponentType<RendererProps>>);

  components.table = renderTable;
  components.hr = () => null;
  // Mermaid記法のコードブロックはStreamdownの処理対象外とする
  components.code = ({
    className,
    children
  }: RendererProps & { className?: string }) => {
    if (
      typeof className === "string" &&
      className.includes("language-mermaid")
    ) {
      return (
        <pre>
          <code>{children}</code>
        </pre>
      );
    }
    return <code className={className}>{children}</code>;
  };

  return (
    <div className={styles.container}>
      <Streamdown
        className={styles.streamdown}
        components={components}
        linkSafety={{ enabled: false }}
      >
        {text}
      </Streamdown>
    </div>
  );
};
